'use client'

import React, { MouseEvent, ReactElement } from 'react'
import { Extra } from '@/types/Misc'
import ChevronRightIcon from '@/icons/font-awesome/sharp-light/chevron-right.svg'
import { useTranslations } from 'next-intl'
import classNames from 'classnames'
import { BookingRequestExtraSelection } from '@/types/Booking'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { faPlus, faMinus } from '@awesome.me/kit-dc19441bb1/icons/sharp/light'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconDefinition } from '@fortawesome/free-solid-svg-icons'
import { Picture } from '@/components/common/picture/Picture'
import { Sizes } from '@/components/common/picture/Sizes'

function HotelExtraButton(props: {
  quantityReached: boolean
  onClick: (event: MouseEvent<HTMLButtonElement>) => void
  icon: ReactElement
}) {
  return (
    <button
      className={classNames(
        'h-10 w-10 rounded-md border border-primary p-2 font-bold text-stone-900',
        { 'opacity-30': props.quantityReached },
      )}
      onClick={props.onClick}
      disabled={props.quantityReached}
    >
      {props.icon}
    </button>
  )
}

export default function HotelExtra(props: {
  extra: Extra
  maxQuantity?: number
  updateBooking?: (data: { Extras: BookingRequestExtraSelection[] }) => void
  bookingSelectionExtras?: BookingRequestExtraSelection[]
  showMoreDetailsClicked: () => void
}): ReactElement | null {
  const t = useTranslations('Hotel.HotelRooms')
  const selectionExtra = getSelectionValue(props.extra.code)
  const maxQuantityReached = selectionExtra?.Quantity === props.maxQuantity
  const minQuantityReached = selectionExtra?.Quantity === 0

  function getSelectionValue(code: string): BookingRequestExtraSelection | undefined {
    return props.bookingSelectionExtras?.find((selection) => selection.ExtraCode === code)
  }

  function handleClickUp(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation()
    event.preventDefault()

    if (
      !selectionExtra ||
      !props.maxQuantity ||
      !props.updateBooking ||
      !props.bookingSelectionExtras
    ) {
      return
    }

    if (selectionExtra.Quantity < props.maxQuantity) {
      selectionExtra.Quantity++
      props.updateBooking({ Extras: [...props.bookingSelectionExtras] })
    }
  }

  function handleClickDown(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation()
    event.preventDefault()

    if (
      !selectionExtra ||
      !props.maxQuantity ||
      !props.updateBooking ||
      !props.bookingSelectionExtras
    ) {
      return
    }

    if (selectionExtra.Quantity > 0) {
      selectionExtra.Quantity--
      props.updateBooking({ Extras: [...props.bookingSelectionExtras] })
    }
  }

  return (
    <>
      <div className="flex h-full flex-grow flex-col overflow-hidden rounded-md bg-white shadow-md">
        <div className="">
          {props.extra.image && props.extra.image !== '' && (
            <Picture
              src={props.extra.image}
              alt=""
              size={{
                [Sizes.xs]: [199, 304, 165],
                [Sizes.sm]: [305, 308, 165],
                [Sizes.md]: [309, 355, 165],
                [Sizes.lg]: [259, 314, 165],
                [Sizes.xl]: [315, 315, 165],
              }}
              cornerRounding={' '}
              staticPosition
            />
          )}
          {(!props.extra.image || props.extra.image === '') && (
            <div className="flex h-[165px] bg-stone-200">
              <div className="flex h-full w-full items-center justify-center">
                <div className="text-sm text-stone-400">{t('pictureDelayText')}</div>
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-grow flex-col p-3">
          <div className="flex-grow">
            <h4 className="mb-1 font-bold leading-normal text-stone-900">{props.extra.name}</h4>
          </div>

          <div className="flex">
            <div
              className="align-center flex cursor-pointer border-b border-white text-sm text-stone-600 hover:underline"
              onClick={props.showMoreDetailsClicked}
            >
              <span>{t('moreDetailsButtonLabel')}</span>
              <ChevronRightIcon className="ml-1 mt-1.5 h-3 w-3 fill-stone-600" />
            </div>
          </div>
          {selectionExtra && (
            <div className="p-4">
              <p className="mb-4 text-center font-bold text-stone-800">{props.extra.price}</p>
              <div className="flex items-center justify-center gap-4">
                <HotelExtraButton
                  quantityReached={minQuantityReached}
                  onClick={handleClickDown}
                  icon={<FontAwesomeIcon icon={faMinus as IconDefinition} />}
                />
                <p className="w-[26px] p-2">{selectionExtra.Quantity}</p>
                <HotelExtraButton
                  quantityReached={maxQuantityReached}
                  onClick={handleClickUp}
                  icon={<FontAwesomeIcon icon={faPlus as IconDefinition} />}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
